import { useCallback, useMemo } from 'react';

import type { TranslationKey, TranslationPluralKey } from '@change-corgi/core/i18n';
import { translationKey, translationPluralKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import type { PsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';

import { useReplacements } from './useReplacements';

const DEFAULT_COPY = {
	headlineText: translationKey('corgi.psf.promote_or_share.headline_without_amount'),
	headlineTextIdentified: translationKey('corgi.psf.promote_or_share.headline_without_amount_identified'),
	progressBarLabelText: translationKey('fe.components.psf.progress_bar_label'),
	shareButtonText: translationKey('fe.components.psf.promote_or_share.share'),
	skipLinkText: translationKey('fe.components.psf.promote_or_share.skip'),
	promoterCountHeading: translationKey('fe.components.psf.promote_or_share.promoter_count_heading'),
	promoterCountText: translationPluralKey('fe.components.psf.promote_or_share.promoter_count_html'),
	subheadingText1: translationKey('corgi.psf.promote_or_share.subheading.signing_step_info_v1'),
	subheadingText2: translationKey('corgi.psf.promote_or_share.subheading.chip_in_request'),
	subheadingText3: translationKey('corgi.psf.promote_or_share.subheading.chip_in_benefit'),
	subheadingContributionHero: translationPluralKey('corgi.psf.promote_or_share.subheading.contribution_hero_v1'),
	subheadingContributionNone: translationKey('corgi.psf.promote_or_share.subheading.contribution_none'),
	impactBoxHeading: translationKey('fe.components.psf.promote_or_share.impact_box_heading'),
	impactBoxMainCopy: translationPluralKey('fe.components.psf.promote_or_share.impact_box_main_copy_html'),
	promoteButtonWithGoalText: translationKey('corgi.psf.promote_or_share.promote_button_with_goal'),
	paymentIconsText: translationKey('corgi.psf.promote_or_share.payment_icons.alt'),
};

type DefaultCopy = typeof DEFAULT_COPY;
export type TranslationName = keyof DefaultCopy;

const DONATION_LANGUAGE_COPY = {
	headlineText: translationKey('corgi.psf.promote_or_share.headline_without_amount'),
	headlineTextIdentified: translationKey('corgi.psf.promote_or_share.headline_without_amount_identified'),
	subheadingText1: translationKey('corgi.psf.promote_or_share.subheading.signing_step_info_v1'),
	subheadingText2: translationKey('corgi.psf.promote_or_share.subheading.chip_in_request_donation'),
	subheadingText3: translationKey('corgi.psf.promote_or_share.subheading.chip_in_benefit_donation'),
	impactBoxMainCopy: translationPluralKey('fe.components.psf.promote_or_share.impact_box_main_copy_donation_html'),
	promoteButtonWithGoalText: translationKey('corgi.psf.promote_or_share.promote_button_with_goal_donation'),
} satisfies Partial<Record<TranslationName, TranslationKey | TranslationPluralKey>>;

type Result = ModelHookResult<
	{ keys: DefaultCopy },
	{ translateKey: (name: TranslationName, key: TranslationKey | TranslationPluralKey) => string }
>;

function translateSpecificKeys({
	name,
	key,
	typedTranslate,
	typedTranslatePlural,
	petition,
	replacements,
	useDonationLanguage,
}: {
	name: TranslationName;
	key: TranslationKey | TranslationPluralKey;
	typedTranslate: (key: TranslationKey, replacements?: Record<string, string>) => string;
	typedTranslatePlural: (key: TranslationPluralKey, count: number, replacements?: Record<string, string>) => string;
	petition: PsfPromoteOrSharePageData['petition'];
	replacements: Record<string, string>;
	useDonationLanguage: boolean;
}): string {
	const {
		promotionStats: { promoterCount, promoterCountLocalized, signatureCount },
	} = petition;
	if (name === 'impactBoxMainCopy') {
		return useDonationLanguage
			? typedTranslatePlural(key as unknown as TranslationPluralKey, signatureCount, replacements)
			: '';
	}
	if (name === 'promoterCountText') {
		return typedTranslatePlural(key as unknown as TranslationPluralKey, promoterCount, replacements);
	}
	if (name === 'subheadingContributionHero') {
		return promoterCount > 0
			? typedTranslatePlural(key as unknown as TranslationPluralKey, promoterCount, {
					contributionCount: promoterCountLocalized,
				})
			: '';
	}
	return typedTranslate(key as unknown as TranslationKey, replacements);
}

export function useCopyI18nKeys(): Result {
	const { typedTranslate, typedTranslatePlural } = useI18n();
	const {
		data: { replacements },
	} = useReplacements();
	const {
		data: { petition, useDonationLanguage },
	} = usePsfPromoteOrSharePageData();

	const translateKey = useCallback(
		(name: TranslationName, key: TranslationKey | TranslationPluralKey): string => {
			return translateSpecificKeys({
				name,
				key,
				typedTranslate,
				typedTranslatePlural,
				petition,
				replacements,
				useDonationLanguage,
			});
		},
		[typedTranslate, typedTranslatePlural, petition, replacements, useDonationLanguage],
	);

	return {
		data: {
			keys: useMemo(
				() => (useDonationLanguage ? { ...DEFAULT_COPY, ...DONATION_LANGUAGE_COPY } : DEFAULT_COPY),
				[useDonationLanguage],
			),
		},
		actions: {
			translateKey,
		},
	};
}
